import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { red, orange } from "@mui/material/colors";
import Header from "./header";
import Footer from "./footer";
import Seo from "./components/Seo";
import seoRoutes from "../seo/seoRoutes";

const ORANGE_ACCENT = orange?.A400 || orange[600] || "#ff9100";

// Reusable header-like background (matches your hero/header)
const headerLikeBg = {
  position: "relative",
  overflow: "hidden",
  backgroundImage: `linear-gradient(135deg, ${alpha(red[600], 0.08)} 0%, ${alpha(
    ORANGE_ACCENT,
    0.08
  )} 100%)`,
  border: (t) => `1px solid ${t.palette.divider}`,
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(rgba(255,255,255,.16) 1px, transparent 1.5px)",
    backgroundSize: "14px 14px",
    pointerEvents: "none",
  },
};

export default function AboutCollegePage() {
  const seo = seoRoutes["/about"];

return (
  <>
    <Seo
      title={seo.title}
      description={seo.description}
      path="/about"
      image={seo.image}
    />

      {/* HERO */}
      <Box
        sx={{
          pt: { xs: 7, sm: 9 },
          pb: { xs: 5, sm: 7 },
          backgroundImage: `linear-gradient(135deg, ${alpha(
            red[600],
            0.10
          )} 0%, ${alpha(ORANGE_ACCENT, 0.10)} 100%)`,
          borderBottom: `1px solid ${alpha(red[600], 0.18)}`,
          position: "relative",
          overflow: "hidden",
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              `radial-gradient(420px 260px at 0% 0%, ${alpha("#fff", .18)} 0%, transparent 60%),
               radial-gradient(460px 280px at 100% 100%, ${alpha("#fff", .10)} 0%, transparent 65%)`,
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h1"
            align="center"
            sx={{
              fontWeight: 900,
              fontSize: "clamp(32px, 5vw, 56px)",
              lineHeight: 1.12,
              letterSpacing: 0.2,
              background: `linear-gradient(90deg, ${red[700]} 0%, ${ORANGE_ACCENT} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            About AISECT Inter College
          </Typography>
        </Container>
      </Box>

      {/* MAIN */}
      <Container maxWidth="lg" sx={{ my: { xs: 3.5, sm: 5 } }}>
        <Grid container spacing={{ xs: 2.5, sm: 3 }}>
          {/* Campus/Image */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ borderRadius: 3, ...headerLikeBg }}>
              <Box
                component="img"
                // Replace with your campus/college photo
                src="/assets/images/campus/college.jpg"
                alt="AISECT Inter College Campus"
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 360, sm: 420 },
                  objectFit: "cover",
                  borderRadius: 3,
                }}
              />
              <CardContent sx={{ pb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: "clamp(18px, 2.2vw, 24px)",
                    mb: 0.25,
                  }}
                >
                  AISECT Inter College
                </Typography>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "clamp(13.5px, 1.6vw, 16px)",
                  }}
                >
                  Matwari, Hazaribagh (Jharkhand)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* About text */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                p: { xs: 2.25, sm: 2.75 },
                borderRadius: 3,
                ...headerLikeBg,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "clamp(20px, 2.6vw, 28px)",
                  mb: 1,
                }}
              >
                Who We Are
              </Typography>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "clamp(16.5px, 1.9vw, 19px)",
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                Established in 2023 at Matwari, Hazaribagh (Jharkhand), AISECT
                Inter College provides quality intermediate education across
                Science, Commerce, and Arts. We blend a learner-centric approach
                with modern infrastructure to help students build strong
                foundations for higher studies and careers.
              </Typography>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "clamp(16.5px, 1.9vw, 19px)",
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                Our faculty brings academic rigor and mentorship, fostering
                curiosity, character, and confidence. Through classroom learning,
                labs, co-curriculars, and community engagement, we aim to develop
                well-rounded individuals ready for the challenges of the 21
                <sup>st</sup> century.
              </Typography>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "clamp(16.5px, 1.9vw, 19px)",
                  lineHeight: 1.9,
                }}
              >
                As a young institution with a bold vision, we continue to expand
                programs, strengthen student support, and build partnerships that
                open doors for every learner. Welcome to a campus where ambition
                meets guidance—and potential turns into achievement.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </>
  );
}
